/**
 * Language Selector Component
 * Reusable language dropdown component for both web and Word interfaces
 *
 * A user's choice reaches the app only through the onLanguageChange /
 * onAutoDetectToggle callbacks. setCurrentLanguage() only updates the display,
 * so a detected language never triggers a reprocess (#125).
 */

import { BaseComponent, type BaseComponentOptions } from './base-component';
import type { LanguageOption } from '../core/language-manager';

// LanguageSelector options interface
export interface LanguageSelectorOptions extends BaseComponentOptions {
  currentLanguage?: string;
  languages?: LanguageOption[];
  autoDetected?: boolean;
  onLanguageChange?: (languageCode: string, autoDetected: boolean) => void;
  onAutoDetectToggle?: (enabled: boolean) => void;
  showAutoDetect?: boolean;
  showNativeNames?: boolean;
  compact?: boolean;
  /** Compact mode only: auto-detect checkbox above a full-width select (web reader sidebar). */
  stacked?: boolean;
  hasDocument?: boolean;
}

class LanguageSelector extends BaseComponent {
  private currentLanguage: string;
  private languages: LanguageOption[];
  private autoDetected: boolean;
  private onLanguageChange: (
    languageCode: string,
    autoDetected: boolean
  ) => void;
  private onAutoDetectToggle: (enabled: boolean) => void;
  private showAutoDetect: boolean;
  private showNativeNames: boolean;
  private compact: boolean;
  private stacked: boolean;
  private hasDocument: boolean;
  private element: HTMLElement | null;
  private selectElement: HTMLSelectElement | null;
  private autoDetectCheckbox: HTMLInputElement | null;
  private currentLanguageDisplay: HTMLElement | null;

  constructor(options: LanguageSelectorOptions = {}) {
    super(options);
    this.currentLanguage = options.currentLanguage || '';
    this.languages = options.languages || [];
    this.autoDetected =
      options.autoDetected !== undefined ? options.autoDetected : true;
    this.onLanguageChange = options.onLanguageChange || (() => {});
    this.onAutoDetectToggle = options.onAutoDetectToggle || (() => {});
    this.showAutoDetect = options.showAutoDetect !== false;
    this.showNativeNames = options.showNativeNames !== false;
    this.compact = options.compact || false;
    this.stacked = options.stacked || false;
    this.hasDocument =
      options.hasDocument !== undefined ? options.hasDocument : false;
    this.element = null;
    this.selectElement = null;
    this.autoDetectCheckbox = null;
    this.currentLanguageDisplay = null;
  }

  protected getDefaultCssClass(): string {
    return 'language-selector';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'language.current': 'Current',
      'language.autoDetect': 'Auto-detect language',
      'language.detected': 'Auto-detected',
    };
  }

  protected override onInitialize(): void {
    this.attachEventListeners();
  }

  /**
   * Render the language selector HTML
   */
  protected override render(): void {
    if (this.compact) {
      this.renderCompact();
    } else {
      this.renderFull();
    }
  }

  /**
   * Render compact layout: one row for a status bar, or stacked (checkbox,
   * then select) for the web reader sidebar.
   */
  renderCompact(): void {
    const compactClass = this.stacked
      ? `${this.cssClass} ${this.baseCssClass}--compact ${this.baseCssClass}--stacked`
      : `${this.cssClass} ${this.baseCssClass}--compact`;

    const autoDetectSection = this.showAutoDetect
      ? `
      <label class="${this.baseCssClass}__auto-detect-label">
        <input type="checkbox"
               class="${this.baseCssClass}__auto-detect-checkbox"
               ${this.autoDetected ? 'checked' : ''}>
        ${this.t('language.autoDetect')}
      </label>
    `
      : '';

    // Show detected badge only when auto-detect is enabled and document is loaded
    const detectedBadge =
      this.autoDetected && this.hasDocument && this.currentLanguage
        ? `<span class="${this.baseCssClass}__detected-badge">${this.t('language.detected')}</span>`
        : '';

    if (this.container) {
      this.container.innerHTML = `
      <div class="${compactClass}">
        ${this.stacked ? autoDetectSection : ''}
        <select id="${this.baseCssClass}-select" class="${this.baseCssClass}__select">
          ${this.renderLanguageOptions()}
        </select>
        ${detectedBadge}
        ${this.stacked ? '' : autoDetectSection}
      </div>
    `;
    }

    // Get references to elements
    const selectSelector = `.${this.baseCssClass}__select`;
    const checkboxSelector = `.${this.baseCssClass}__auto-detect-checkbox`;

    // For the main element, use the full CSS class string (which may have multiple classes)
    const cssClassForSelector = compactClass.replace(/\s+/g, '.');
    const mainSelector = `.${cssClassForSelector}`;

    this.element = this.container?.querySelector(mainSelector) || null;
    this.selectElement = this.container?.querySelector(selectSelector) || null;
    this.autoDetectCheckbox =
      this.container?.querySelector(checkboxSelector) || null;
    this.currentLanguageDisplay = null; // Not used in compact mode

    // Update auto-detect state
    this.updateAutoDetectState();
  }

  /**
   * Render full vertical layout for sidebar
   */
  renderFull(): void {
    const autoDetectSection = this.showAutoDetect
      ? `
      <div class="${this.baseCssClass}__auto-detect">
        <label class="${this.baseCssClass}__auto-detect-label">
          <input type="checkbox"
                 class="${this.baseCssClass}__auto-detect-checkbox"
                 ${this.autoDetected ? 'checked' : ''}>
${this.t('language.autoDetect')}
        </label>
      </div>
    `
      : '';

    const currentLanguageSection = this.hasDocument
      ? `
      <div class="${this.baseCssClass}__current">
        <span class="${this.baseCssClass}__current-label">${this.t('language.current')}:</span>
        <span class="${this.baseCssClass}__current-language"></span>
        ${this.autoDetected ? `<span class="${this.baseCssClass}__detected-badge">${this.t('language.detected')}</span>` : ''}
      </div>
    `
      : `
      <div class="${this.baseCssClass}__current-simple">
        <span class="${this.baseCssClass}__current-label">${this.t('language.current')}:</span>
        <span class="${this.baseCssClass}__current-language"></span>
      </div>
    `;

    if (this.container) {
      this.container.innerHTML = `
      <div class="${this.cssClass}">
        ${currentLanguageSection}

        <select id="${this.baseCssClass}-select" class="${this.baseCssClass}__select">
          ${this.renderLanguageOptions()}
        </select>

        ${autoDetectSection}
      </div>
    `;
    }

    // Get references to elements
    const selectSelector = `.${this.baseCssClass}__select`;
    const checkboxSelector = `.${this.baseCssClass}__auto-detect-checkbox`;
    const displaySelector = `.${this.baseCssClass}__current-language`;

    // For the main element, use the full CSS class string (which may have multiple classes)
    const cssClassForSelector = this.cssClass.replace(/\s+/g, '.');
    const mainSelector = `.${cssClassForSelector}`;

    this.element = this.container?.querySelector(mainSelector) || null;
    this.selectElement = this.container?.querySelector(selectSelector) || null;
    this.autoDetectCheckbox =
      this.container?.querySelector(checkboxSelector) || null;
    this.currentLanguageDisplay =
      this.container?.querySelector(displaySelector) || null;

    // Update current language display and auto-detect state
    this.updateCurrentLanguageDisplay();
    this.updateAutoDetectState();
  }

  /**
   * Render language option elements
   * @returns HTML options string
   */
  renderLanguageOptions(): string {
    const emptyOption =
      !this.hasDocument || !this.currentLanguage
        ? '<option value="" selected></option>'
        : '';

    const languageOptions = this.languages
      .map((lang) => {
        const label = this.showNativeNames ? lang.nativeName : lang.name;

        const selected = lang.code === this.currentLanguage ? 'selected' : '';

        return `<option value="${lang.code}" ${selected}>${label}</option>`;
      })
      .join('');

    return emptyOption + languageOptions;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners(): void {
    if (this.selectElement) {
      this.selectElement.addEventListener('change', (event) => {
        this.handleLanguageChange((event.target as HTMLSelectElement).value);
      });
    }

    if (this.autoDetectCheckbox) {
      this.autoDetectCheckbox.addEventListener('change', (event) => {
        this.handleAutoDetectToggle((event.target as HTMLInputElement).checked);
      });
    }
  }

  /**
   * Handle language selection change
   * @param languageCode - Selected language code
   */
  handleLanguageChange(languageCode: string): void {
    if (languageCode !== this.currentLanguage) {
      this.currentLanguage = languageCode;
      this.autoDetected = false; // Manual selection disables auto-detect
      this.updateCurrentLanguageDisplay();
      this.updateAutoDetectState();

      // The one reprocess path (#125).
      this.onLanguageChange(languageCode, false);
    }
  }

  /**
   * Handle auto-detect toggle
   * @param enabled - Whether auto-detect is enabled
   */
  handleAutoDetectToggle(enabled: boolean): void {
    this.autoDetected = enabled;

    // Directly disable/enable the dropdown based on auto-detect state
    if (this.selectElement) {
      this.selectElement.disabled = enabled; // enabled = true → dropdown disabled
    }

    this.updateCurrentLanguageDisplay();
    this.updateAutoDetectState(); // Update checkbox and ensure dropdown state

    this.onAutoDetectToggle(enabled);
  }

  /**
   * Update current language display
   */
  updateCurrentLanguageDisplay(): void {
    // Update the display element if it exists (full mode only)
    if (this.currentLanguageDisplay) {
      if (this.hasDocument && this.currentLanguage) {
        const currentLang = this.languages.find(
          (lang) => lang.code === this.currentLanguage
        );
        if (currentLang) {
          const displayName = this.showNativeNames
            ? currentLang.nativeName
            : currentLang.name;
          this.currentLanguageDisplay.textContent = displayName;
        }
      } else {
        // No document loaded or no language detected - show blank
        this.currentLanguageDisplay.textContent = '';
      }
    }

    // Update detected badge visibility and text (only if container is initialized)
    if (this.container) {
      const detectedBadge = this.container.querySelector(
        `.${this.baseCssClass}__detected-badge`
      );
      if (detectedBadge) {
        // Show badge only when auto-detect is enabled, document is loaded, AND language is detected
        const shouldShow =
          this.autoDetected && this.hasDocument && this.currentLanguage;
        (detectedBadge as HTMLElement).style.display = shouldShow
          ? 'inline'
          : 'none';
        detectedBadge.textContent = this.t('language.detected');
      }
    }

    // Update current label text (only if container is initialized and element exists)
    if (this.container) {
      const currentLabel = this.container.querySelector(
        `.${this.baseCssClass}__current-label`
      );
      if (currentLabel) {
        currentLabel.textContent = this.t('language.current') + ':';
      }
    }
  }

  /**
   * Update auto-detect checkbox state and dropdown availability
   */
  updateAutoDetectState(): void {
    if (this.autoDetectCheckbox) {
      this.autoDetectCheckbox.checked = this.autoDetected;
    }

    // Enable/disable dropdown based on auto-detect state
    if (this.selectElement) {
      this.selectElement.disabled = this.autoDetected;
    }

    // Update auto-detect label text (only if container is initialized)
    if (this.container) {
      const autoDetectLabel = this.container.querySelector(
        `.${this.baseCssClass}__auto-detect-label`
      );
      if (autoDetectLabel && autoDetectLabel.childNodes) {
        const textContent =
          (autoDetectLabel as HTMLElement).textContent ||
          (autoDetectLabel as HTMLElement).innerText;
        // Only update if the text content doesn't already match the current translation
        const expectedText = this.t('language.autoDetect');
        if (!textContent.includes(expectedText)) {
          // Find the text node and update it
          const textNode = Array.from(autoDetectLabel.childNodes).find(
            (node) =>
              node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
          );
          if (textNode) {
            textNode.textContent = expectedText;
          }
        }
      }
    }
  }

  /**
   * Update UI text when language changes
   */
  override updateUILanguage(): void {
    this.updateCurrentLanguageDisplay();
    this.updateAutoDetectState();
  }

  /**
   * Set current language programmatically (display only, no callback)
   * @param languageCode - Language code
   * @param autoDetected - Whether this was auto-detected (preserves current state if not specified)
   */
  setCurrentLanguage(
    languageCode: string,
    autoDetected: boolean | undefined = undefined
  ): void {
    this.currentLanguage = languageCode;
    // Only update autoDetected state if explicitly provided
    if (autoDetected !== undefined) {
      this.autoDetected = autoDetected;
    }

    // Update select element
    if (this.selectElement) {
      this.selectElement.value = languageCode;
    }

    this.updateCurrentLanguageDisplay();
    this.updateAutoDetectState();
  }

  /**
   * Set whether a document is loaded (affects display)
   * @param hasDocument - Whether a document is loaded
   */
  setDocumentLoaded(hasDocument: boolean): void {
    const wasChanged = this.hasDocument !== hasDocument;
    this.hasDocument = hasDocument;

    // If no document, reset current language
    if (!hasDocument) {
      this.currentLanguage = '';
      if (this.selectElement) {
        this.selectElement.value = '';
      }
    }

    // Re-render if document state changed to show/hide detected badge
    if (wasChanged && this.container) {
      this.render();
      this.attachEventListeners();
    } else {
      this.updateCurrentLanguageDisplay();
      this.updateAutoDetectState();
    }
  }

  /**
   * Show/hide the language selector
   * @param visible - Whether to show the selector
   */
  setVisible(visible: boolean): void {
    if (this.element) {
      this.element.style.display = visible ? 'block' : 'none';
    }
  }

  /**
   * Enable/disable the language selector
   * @param enabled - Whether to enable the selector
   */
  setEnabled(enabled: boolean): void {
    // For the dropdown, consider both the enabled state and autodetect state
    if (this.selectElement) {
      // Dropdown is disabled if the component is disabled OR autodetect is enabled
      this.selectElement.disabled = !enabled || this.autoDetected;
    }

    if (this.autoDetectCheckbox) {
      this.autoDetectCheckbox.disabled = !enabled;
    }
  }

  /**
   * Get current language code
   * @returns Current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Check if auto-detect is enabled
   * @returns True if auto-detect is enabled
   */
  isAutoDetectEnabled(): boolean {
    return this.autoDetected;
  }

  /**
   * Destroy the language selector
   */
  override destroy(): void {
    this.element = null;
    this.selectElement = null;
    this.autoDetectCheckbox = null;
    this.currentLanguageDisplay = null;
    super.destroy();
  }
}

export default LanguageSelector;
